import { useEffect, useState, type CSSProperties, type ReactElement } from "react";
import { Clock, ListTodo } from "lucide-react";
import { color, radius, space } from "@tokens/index";
import { Dialog } from "@primitives/overlay";
import { SkeletonRows } from "@primitives/Skeleton";
import { Text } from "@primitives/Text";
import type { WorkItem } from "../../experience-shell/workflows";
import { WorkItemRowContent } from "./QueueWorkItems";

// ---------------------------------------------------------------------------
// QueueWorkItemsDialog — the drill-in behind a queue's Open / Pending badge.
//
// Presentational: the experience shell owns the data fetch and passes the
// resolved items in. Rows reuse `WorkItemRowContent` so a row here matches
// the queue worklist panel. Read-only: rows carry no navigation.
// ---------------------------------------------------------------------------

export type QueueWorkItemsBucket = "open" | "pending";

export interface QueueWorkItemsDialogProps {
    readonly isOpen: boolean;
    readonly queueName: string;
    readonly bucket: QueueWorkItemsBucket;
    readonly items: readonly WorkItem[];
    readonly totalCount: number;
    readonly isLoading: boolean;
    readonly error?: Error | null;
    readonly onClose: () => void;
}

const BUCKET_TITLE: Record<QueueWorkItemsBucket, string> = {
    open: "Open work items",
    pending: "Pending work items",
};

const BUCKET_NOUN: Record<QueueWorkItemsBucket, string> = {
    open: "open",
    pending: "pending",
};

const listStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: space[3],
};

const rowStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: space[2],
    minWidth: 0,
    padding: `${space[3]} ${space[4]}`,
    borderRadius: radius.lg,
    border: `1px solid ${color.border.subtle}`,
    background: color.bg.surface2,
};

const errorStyle: CSSProperties = {
    padding: `${space[3]} ${space[4]}`,
    borderRadius: radius.md,
    border: `1px solid ${color.accent.danger}`,
    background: color.status.dangerBg,
};

const SKELETON_ROW_COUNT = 4;

export function QueueWorkItemsDialog({
    isOpen,
    queueName,
    bucket,
    items,
    totalCount,
    isLoading,
    error,
    onClose,
}: QueueWorkItemsDialogProps): ReactElement {
    // Coarse clock so relative labels in each row stay live while open.
    const [now, setNow] = useState<Date>(() => new Date());
    useEffect(() => {
        if (!isOpen) return;
        const id = setInterval(() => {
            setNow(new Date());
        }, 30_000);
        return () => {
            clearInterval(id);
        };
    }, [isOpen]);

    const TitleIcon = bucket === "open" ? ListTodo : Clock;
    const countLabel = `${String(totalCount)} ${BUCKET_NOUN[bucket]} item${totalCount === 1 ? "" : "s"}`;
    const description =
        !isLoading && totalCount > 0 ? `${queueName} \u00b7 ${countLabel}` : queueName;

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            title={BUCKET_TITLE[bucket]}
            description={description}
            icon={<TitleIcon size={20} strokeWidth={2} />}
            size="lg"
            presentation="auto"
            testId="queue-workitems-dialog"
        >
            {error !== null && error !== undefined ? (
                <div style={errorStyle} role="alert">
                    <Text role="bodySm">Couldn&rsquo;t load work items. {error.message}</Text>
                </div>
            ) : isLoading && items.length === 0 ? (
                <SkeletonRows
                    rows={SKELETON_ROW_COUNT}
                    shape="card"
                    rowHeight={88}
                    label="Loading work items"
                />
            ) : items.length === 0 ? (
                <Text role="bodySm" tone="muted" style={{ padding: `${space[4]} 0` }}>
                    {bucket === "open"
                        ? "No open work items in this queue right now."
                        : "No pending work items in this queue right now."}
                </Text>
            ) : (
                <div
                    role="list"
                    aria-label={`${BUCKET_TITLE[bucket]} in ${queueName}`}
                    style={listStyle}
                >
                    {items.map((item) => (
                        <div key={item.id} role="listitem" style={rowStyle}>
                            <WorkItemRowContent
                                item={item}
                                now={now}
                                onNavigateToGalileoEntity={undefined}
                                renderPresence={undefined}
                            />
                        </div>
                    ))}
                </div>
            )}
        </Dialog>
    );
}
